import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/core/firebase';
import { addTrainerContact } from '../../lib/contacts';
import { Panel } from '../ui/Panel';
import { Button } from '../ui/Button';
import { Avatar } from '../ui/Avatar';
import { useAuth } from '@/core/AuthContext';
import { useTrainerDetails } from '@/lib/useTrainerDetails';

interface TrainerContactSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Check if environment is dev/staging or user is admin
 */
const isDevOrAdmin = () => {
  const isDev = import.meta.env.VITE_APP_ENV === 'development' || 
                import.meta.env.VITE_APP_ENV === 'staging' ||
                import.meta.env.DEV;
  const userRole = localStorage.getItem('userRole') || '';
  return isDev || userRole === 'admin';
};

/**
 * Bottom-sheet / modal showing the user's trainer contact card.
 * Displays avatar, name, persona, tap-to-call phone link, email, and a
 * vCard download so the user can save the trainer to their contacts.
 */
export function TrainerContactSheet({ isOpen, onClose }: TrainerContactSheetProps) {
  const { userData } = useAuth();
  const trainerDetails = useTrainerDetails({
    trainerId: userData?.trainerId || userData?.trainerProfileId,
    aiTrainerId: userData?.aiTrainerId,
    enabled: isOpen && Boolean(userData),
  });
  const [testCallLoading, setTestCallLoading] = useState(false);
  const [testCallStatus, setTestCallStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [testCallError, setTestCallError] = useState<string>('');
  const [contactStatus, setContactStatus] = useState<'idle' | 'adding' | 'added' | 'denied' | 'error'>('idle');

  const handleAddContact = async () => {
    if (!trainerDetails.name) return;

    setContactStatus('adding');
    try {
      const result = await addTrainerContact({
        name: trainerDetails.name || 'Your Trainer',
        phone: trainerDetails.phone,
        email: trainerDetails.email,
        company: trainerDetails.company,
        persona: trainerDetails.persona,
        photoURL: trainerDetails.photoURL,
        aiTrainerId: userData?.aiTrainerId,
      });

      setContactStatus(result === 'denied' ? 'denied' : 'added');
      if (result !== 'denied') {
        setTimeout(() => setContactStatus('idle'), 3000);
      }
    } catch (err) {
      console.error('[TrainerContactSheet] Failed to add contact:', err);
      setContactStatus('error');
      setTimeout(() => setContactStatus('idle'), 3000);
    }
  };

  const handleTestCall = async () => {
    if (!functions) return;
    
    setTestCallLoading(true);
    setTestCallStatus('idle');
    setTestCallError('');
    
    try {
      const sendCallReminder = httpsCallable(functions, 'sendCallReminder');
      await sendCallReminder({});
      setTestCallStatus('success');
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setTestCallStatus('idle');
      }, 5000);
    } catch (error: any) {
      console.error('Test call failed:', error);
      setTestCallStatus('error');
      setTestCallError(error.message || 'Unknown error');
    } finally {
      setTestCallLoading(false);
    }
  };

  return (
    <Panel isOpen={isOpen} onClose={onClose} title="Your Trainer" variant="modal" size="sm">
      {trainerDetails.loading && (
        <div className="flex items-center justify-center py-12 text-gray-500 dark:text-slate-500">
          Loading…
        </div>
      )}

      {!trainerDetails.loading && !trainerDetails.name && (
        <div className="flex items-center justify-center py-12 text-sm text-gray-500 dark:text-slate-500">
          Trainer info unavailable.
        </div>
      )}

      {!trainerDetails.loading && trainerDetails.name && (
        <div className="space-y-5">
          {/* Avatar + name */}
          <div className="flex items-center gap-4">
            <Avatar
              src={trainerDetails.photoURL}
              alt={trainerDetails.name}
              fallback={trainerDetails.name}
              size="xl"
              className="w-16 h-16 flex-shrink-0"
            />
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {trainerDetails.name}
              </h2>
              <p className="text-sm text-gray-500 dark:text-slate-400">{trainerDetails.label}</p>
            </div>
          </div>

          {/* Persona quote */}
          {trainerDetails.persona && (
            <div className="bg-primary-50 dark:bg-primary-500/10 rounded-xl p-4">
              <p className="text-sm italic text-primary-900 dark:text-primary-300">
                "{trainerDetails.persona}"
              </p>
            </div>
          )}

          {/* Contact rows - only show if configured */}
          {(trainerDetails.phone || trainerDetails.email) && (
            <div className="divide-y divide-gray-200 dark:divide-white/10 rounded-xl border border-gray-200 dark:border-white/10 overflow-hidden">
              {/* Phone — tap to call */}
              {trainerDetails.phone && (
                <a
                  href={`tel:${trainerDetails.phone}`}
                  className="flex items-center gap-4 px-4 py-3 bg-white dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/10 transition group"
                >
                  <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30 flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-green-600 dark:text-green-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 dark:text-slate-500">Trainer phone</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400 transition">
                      {trainerDetails.phone}
                    </p>
                  </div>
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 flex-shrink-0">
                    Call
                  </span>
                </a>
              )}

              {/* Email */}
              {trainerDetails.email && (
                <a
                  href={`mailto:${trainerDetails.email}`}
                  className="flex items-center gap-4 px-4 py-3 bg-white dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/10 transition group"
                >
                  <div className="p-2 rounded-lg bg-accent-100 dark:bg-accent-900/30 flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-accent-600 dark:text-accent-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 dark:text-slate-500">Email</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-accent-600 dark:group-hover:text-accent-400 transition truncate">
                      {trainerDetails.email}
                    </p>
                  </div>
                </a>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="secondary"
                onClick={handleAddContact}
                disabled={contactStatus === 'adding' || contactStatus === 'added'}
                isLoading={contactStatus === 'adding'}
                className="flex-1"
              >
                {contactStatus !== 'adding' && (
                  <svg
                    className="w-4 h-4 mr-2 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    {contactStatus === 'added' ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    ) : (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                      />
                    )}
                  </svg>
                )}
                {contactStatus === 'added' ? 'Contact Added' : 'Add to Contacts'}
              </Button>
              
              {isDevOrAdmin() && (
                <Button 
                  variant="outline" 
                  onClick={handleTestCall}
                  isLoading={testCallLoading}
                  disabled={testCallLoading}
                  className="flex-1"
                >
                  <svg
                    className="w-4 h-4 mr-2 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  Test Call
                </Button>
              )}
            </div>
            
            {contactStatus === 'denied' && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Contacts access was denied. Open Settings to grant permission.
                </p>
              </div>
            )}

            {contactStatus === 'error' && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-700 dark:text-red-300">
                  Failed to add contact. Please try again.
                </p>
              </div>
            )}

            {testCallStatus === 'success' && (
              <div className="bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg p-3">
                <p className="text-sm text-success-700 dark:text-success-300">
                  ✓ Test call initiated! You should receive a call shortly.
                </p>
              </div>
            )}
            
            {testCallStatus === 'error' && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-700 dark:text-red-300">
                  ✗ Failed to initiate call: {testCallError}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
}
